import { useEffect, useRef } from 'react'
import { useHitTarget } from '../responsive/hitTarget'
import './checkbox.css'

type Props = {
  checked: boolean | 'mixed'
  onChange: (next: boolean) => void
  /** The accessible name. There is no visible label: a checkbox in a grid row is named by its row. */
  label: string
  /** Roving-tabindex hosts (`DataGrid`) manage this themselves. */
  tabIndex?: number
  disabled?: boolean
  describedBy?: string
}

/**
 * A skinned checkbox: a real `<input type="checkbox">`, visually hidden but
 * stretched over the hit target, with the visible box drawn by a sibling.
 *
 * Native controls are invisible to every colour guard — an absent declaration
 * is unscannable, and system-blue radios have shipped twice — so every pixel a
 * user sees here is a class in `checkbox.css`.
 *
 * **The state is never colour alone.** A checked box draws a tick and a mixed
 * one a dash, both inside the lime fill, so a user who cannot tell lime from
 * white still reads the state off the glyph.
 *
 * `mixed` is exposed as `aria-checked="mixed"` and as the DOM `indeterminate`
 * property. Activating a mixed box reports `true`: "select what is not yet
 * selected" is the safer reading of a click on a partial selection.
 */
export function Checkbox({ checked, onChange, label, tabIndex, disabled, describedBy }: Props) {
  const target = useHitTarget('page')
  const ref = useRef<HTMLInputElement>(null)
  const mixed = checked === 'mixed'

  // `indeterminate` is a DOM property with no attribute, so React cannot set it.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed
  }, [mixed])

  return (
    // The floor is inline, because only an inline declaration reliably outranks
    // whatever else touches the box (#90).
    <span className="checkbox" style={{ minHeight: target, minWidth: target }}>
      <input
        ref={ref}
        type="checkbox"
        className="checkbox__input"
        checked={checked === true}
        aria-checked={mixed ? 'mixed' : checked}
        aria-label={label}
        aria-describedby={describedBy}
        tabIndex={tabIndex}
        disabled={disabled}
        onChange={(e) => onChange(mixed ? true : e.target.checked)}
      />
      <span
        className={`checkbox__box${checked === false ? '' : ' checkbox__box--on'}`}
        aria-hidden="true"
      >
        {checked !== false && (
          <svg className="checkbox__mark" viewBox="0 0 12 12" width="12" height="12" focusable="false">
            <path d={mixed ? 'M2.5 6h7' : 'M2.5 6.5l2.5 2.5 4.5-5.5'} />
          </svg>
        )}
      </span>
    </span>
  )
}
